import PropTypes from "prop-types";
import {Kebab24, Up24, Down24} from "@bphxd/ds-core-react/lib/icons";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Button,
} from "reactstrap";
import {Link, useNavigate, useParams} from "react-router-dom";
import {formatNumber, useUserSettings} from "providers/userSettings";
import parseAndRoundToTwoDecimal from "utils/numberUtil";
import moment from "moment";
import {docStatus} from "content/DocStatus";
import {documentStatus} from "constants/docManager";
import HighLightedText from "../../HighLightedText";
import "./index.css";

const getFormattedDecimal = (cell) => {
  const {
    userSettings: {decimalFormat},
  } = useUserSettings();
  if (cell.getValue()) {
    return formatNumber(
      parseAndRoundToTwoDecimal(cell.getValue()),
      decimalFormat,
      2,
    );
  }
  return cell.getValue();
};

const RenderLink = (
  id,
  text,
  pageNo,
  cell,
  selectedCountry,
  siteReferenceId,
  divisionId,
) => {
  const {country} = useParams();
  return (
    <Link
      to={{
        pathname: `/document-manager/incoming/${selectedCountry}/detailedView/${id}`,
        search: `?pageNo=${pageNo}&siteReferenceId=${siteReferenceId}&divisionId=${divisionId}`,
      }}
      className="link-dark whitespace-nowrap"
    >
      <u>
        <HighLightedText
          value={text}
          globalFilter={cell.getContext().table.getState().globalFilter}
        />
      </u>
    </Link>
  );
};

const RenderDropdown = ({
  certificateInboundEuId,
  selectedCountry,
  pageNo,
  siteReferenceId,
  divisionId,
}) => {
  const navigate = useNavigate();
  const {country} = useParams();

  return (
    <UncontrolledDropdown
      direction="down"
      className="doc-manager-action-dropdown"
    >
      <DropdownToggle
        color="standard-quartenary"
        className="!px-0 doc-manager-action-dropdown-button"
      >
        <Kebab24></Kebab24>
      </DropdownToggle>
      <DropdownMenu className="dropdown-menu" container="body">
        <DropdownItem
          onClick={() =>
            navigate({
              pathname: `/document-manager/incoming/${selectedCountry}/detailedView/${encodeURIComponent(
                certificateInboundEuId,
              )}`,
              search: `?pageNo=${pageNo}&siteReferenceId=${siteReferenceId}&divisionId=${divisionId}`,
            })
          }
        >
          View document
        </DropdownItem>
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

RenderDropdown.propTypes = {
  certificateInboundEuId: PropTypes.string,
  selectedCountry: PropTypes.string,
  pageNo: PropTypes.number,
  siteReferenceId: PropTypes.string,
  divisionId: PropTypes.string,
};

const renderStatus = (text, cell) => {
  const {className: defaultClassName, message: defaultMessage} =
    docStatus.failed_to_process;
  const {className = defaultClassName, message = defaultMessage} =
    docStatus[text] || {};

  return (
    <div className="flex items-center  ">
      <span
        className={`${className} whitespace-nowrap uppercase  status-badge-doc-manager`}
      >
        <HighLightedText
          value={message}
          globalFilter={cell.getContext().table.getState().globalFilter}
        />
      </span>
    </div>
  );
};

const getColumnsUK = (
  dateFormat,
  decimalFormatNew,
  selectedCountry,
  siteReferenceId,
  divisionId,
  divisionCode,
) => {
  return [
    {
      header: "",
      key: "expandCollapse",
      accessorKey: "splitDetails",
      visible: true,
      enableSorting: false,
      size: 20,
      cell: ({row}) => {
        return row.getCanExpand() ? (
          <Button
            onClick={row.getToggleExpandedHandler()}
            color="standard-quartenary"
            className="!px-0"
          >
            {row.getIsExpanded() ? <Up24 /> : <Down24 />}
          </Button>
        ) : (
          ""
        );
      },
    },
    {
      header: "Document",
      accessorKey: "sdNumber",
      key: "sdNumber",
      disabled: true,
      cell: ({row, getValue, table, cell}) => {
        return RenderLink(
          encodeURIComponent(row.original.certificateInboundEuId),
          getValue(),
          table.getState()?.pagination?.pageIndex,
          cell,
          selectedCountry,
          siteReferenceId,
          divisionId,
        );
      },
      size: 250,
      maxSize: 400,
      visible: true,
    },
    {
      header: "Supplier",
      accessorKey: "supplierName",
      key: "supplierName",
      size: 150,
      disabled: true,
      visible: true,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Receiving point",
      accessorKey: "recipientReceiptAddress",
      key: "recipientReceiptAddress",
      size: 200,
      disabled: true,
      visible: true,
      cell: ({cell, row}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Product",
      accessorKey: "productType",
      key: "productType",
      disabled: true,
      visible: true,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Raw material",
      accessorKey: "rawMaterialType",
      key: "rawMaterialType",
      size: 110,
      disabled: true,
      visible: true,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Quantity m³",
      accessorKey: "feedstockQty",
      key: "feedstockQty",
      size: 100,
      disabled: true,
      visible: true,
      dataType: "number",
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={getFormattedDecimal(cell)}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Mass balance",
      accessorKey: "balance",
      key: "balance",
      size: 200,
      disabled: true,
      visible: true,
      accessorFn: ({mbPeriodName, mbLocationGroupName, state}) => {
        return (
          state === documentStatus.COMPLETED &&
          `${mbPeriodName} - ${mbLocationGroupName}`
        );
      },
      cell: ({
        row: {
          original: {
            mbBalanceGroupId,
            mbBalanceGroupName,
            mbLocationGroupId,
            mbLocationGroupName,
            mbPeriodName,
            mbPeriodId,
            mbPeriodFrom,
            mbPeriodTo,
            state,
          },
        },
        cell,
      }) =>
        state === documentStatus.COMPLETED &&
        mbLocationGroupName &&
        mbPeriodName ? (
          <Link
            to={`/mass-balance/${encodeURIComponent(
              selectedCountry,
            )}/${divisionCode}/TRS/${mbLocationGroupName}/${mbPeriodName}/${mbBalanceGroupName}`}
            className="link-dark"
            state={{mbPeriodId}}
          >
            <u>{cell.getValue()}</u>
          </Link>
        ) : (
          ""
        ),
    },
    {
      header: "GHG savings %",
      accessorKey: "ghgTotal",
      key: "ghgTotal",
      size: 110,
      visible: false,
      dataType: "number",
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={getFormattedDecimal(cell)}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Country of origin",
      accessorKey: "countryCode",
      key: "countryCode",
      size: 200,
      visible: false,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Scheme",
      accessorKey: "certificationSystem",
      key: "certificationSystem",
      size: 200,
      visible: false,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },
    {
      header: "Address of dispatch",
      accessorKey: "supplierDispatchAddress",
      key: "supplierDispatchAddress",
      size: 200,
      visible: false,
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={cell.getValue()}
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },

    {
      header: "Date of dispatch",
      accessorKey: "materialDispatchDatez",
      key: "materialDispatchDatez",
      size: 200,
      visible: false,
      filterFn: "dateRangeFilterFn",
      cell: ({cell}) => {
        return (
          <HighLightedText
            value={
              cell.getValue() && moment(cell.getValue()).format(dateFormat)
            }
            globalFilter={cell.getContext().table.getState().globalFilter}
          />
        );
      },
    },

    {
      header: "Status",
      key: "state",
      disabled: true,
      accessorKey: "state",
      visible: true,
      draggaingDisabled: true,
      cell: ({getValue, cell}) => renderStatus(getValue(), cell),
    },
    {
      header: "",
      accessorKey: "actions",
      key: "actions",
      size: 25,
      visible: true,
      enableSorting: false,
      cell: ({
        row: {
          original: {certificateInboundEuId},
        },
        table,
      }) => (
        <RenderDropdown
          certificateInboundEuId={certificateInboundEuId}
          selectedCountry={selectedCountry}
          pageNo={table.getState()?.pagination?.pageIndex}
          siteReferenceId={siteReferenceId}
          divisionId={divisionId}
        />
      ),
    },
  ];
};

export default getColumnsUK;
